import { useForm } from "react-hook-form";
import { useState } from "react";
import { toast } from "react-toastify";
import { toastifyConfig } from "./ReferralForm/ReferralForm.common";
import { Stack } from "@mui/material";
import { ReactComponent as SuccessIcon } from "images/success.svg";

const isDev = process.env.REACT_APP_CURRENT_ENV === "dev";

export const required = "is required";

export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  //value: /^\S+@\S+$/i,
  message: "Invalid email address",
};

export const emailValidation = {
  required,
  pattern: emailPattern,
};

export const modes = {
  LOGIN: "LOGIN",
  PASSWORD: "PASSWORD",
  LOGIN_WITH_CODE: "LOGIN_WITH_CODE",
  REGISTER: "REGISTER",
};

export const useLogin = ({ setError, reloadButtons, setLoading }) => {
  const [mode, setMode] = useState(modes.LOGIN);
  const formData = useForm({
    defaultValues: {
      username: "",
      password: "",
      otp: "",
    },
  });

  const [isChecking, setIsChecking] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const usernameRegisterProps = formData.register("username", emailValidation);
  const passwordRegisterProps = formData.register("password");
  const otpRegisterProps = formData.register("otp");

  const onEmailSubmit = async (newData) => {
    if (!newData) return;
    setIsChecking(true);
    try {
      const { username } = newData;
      const response = await fetch(
        `${
          process.env.REACT_APP_API_V2_URL
        }/public/users/password-exists-by-email/${encodeURI(username)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const result = await response.json();
      if (response?.status !== 200 || result?.error) {
        setError(result?.message || result?.error);
        setIsChecking(false);
        return;
      }

      if (result?.userExists && result?.passwordSet) {
        formData.setValue("password", "");
        setMode(modes.PASSWORD);
        return;
      }

      if (!result?.userExists) {
        window.location.href = `https://app${
          isDev ? "-dev" : ""
        }.referral-stream.com/sign_up/sender`;
        return;
      }

      if (result?.userExists && !result?.passwordSet) {
        formData.setValue("otp", "");
        setMode(modes.LOGIN_WITH_CODE);
        return;
      }

      setError("Something went wrong. Call support");
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setIsChecking(false);
    }
  };

  const onPasswordSubmit = async (newData) => {
    if (!newData) return;
    setIsChecking(true);
    try {
      const { username: login, password } = newData;

      const response = await fetch(`${process.env.REACT_APP_API_V2_URL}/auth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ login, password }),
      });
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        setError(result?.message || result?.error);
        setIsChecking(false);
        return;
      }

      if (result?.dashboardAccessToken) {
        window.localStorage.setItem(`accessToken`, result.dashboardAccessToken);
        window.localStorage.setItem(`buttonTokens`, JSON.stringify([]));
        setLoading(true);
        reloadButtons();
        return;
      }

      setError("Something went wrong. Call support");
      setIsChecking(false);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
      setIsChecking(false);
    }
  };

  const onOtpSubmit = async (newData) => {
    if (!newData) return;
    setIsChecking(true);
    try {
      const { username: login, otp } = newData;

      const response = await fetch(`${process.env.REACT_APP_API_V2_URL}/auth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ login, otp }),
      });
      const result = await response.json();

      if (response?.status !== 200 || result?.error) {
        setError(result?.message || result?.error);
        setIsChecking(false);
        return;
      }

      if (result?.dashboardAccessToken) {
        window.localStorage.setItem(`accessToken`, result.dashboardAccessToken);
        window.localStorage.setItem(`buttonTokens`, JSON.stringify([]));
        window.localStorage.setItem(`${login}-is-password-not-set`, "true");
        setLoading(true);
        reloadButtons();
        return;
      }

      setError("Something went wrong. Call support");
      setIsChecking(false);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
      setIsChecking(false);
    }
  };

  const resendOtpCode = () => {
    try {
      setError("");
      const { username } = formData.getValues();
      fetch(
        `${process.env.REACT_APP_API_V2_URL}/public/users/${username}/send-otp`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      toast(
        () => (
          <Stack direction={"row"} gap={1}>
            <SuccessIcon />
            <Stack>
              OTP sent successfully
              <div className="text-sm">Please check your mail box.</div>
            </Stack>
          </Stack>
        ),
        toastifyConfig
      );
    } catch (error) {
      setError(error?.message ?? "Failed to resend OTP");
    }
  };

  return {
    formData,
    onEmailSubmit,
    onPasswordSubmit,
    onOtpSubmit,
    usernameRegisterProps,
    passwordRegisterProps,
    otpRegisterProps,
    isPasswordVisible,
    setIsPasswordVisible,
    isChecking,
    mode,
    setMode,
    resendOtpCode,
  };
};
